import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const TermsAndConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center space-x-2 text-slate-300 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </Link>
          <h1 className="text-4xl font-bold">Terms &amp; Conditions</h1>
          <p className="text-slate-300 mt-2">
            Welcome to <a href="https://smartbeeps.com/">Smartbeeps</a>. These Terms and Conditions govern your access to and use of our communication infrastructure, APIs, and software services. By accessing our platform, you agree to comply with and be bound by the following terms.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 space-y-8 text-slate-700">
          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">1. Platform Services</h2>
            <p className="text-sm leading-relaxed text-slate-600">
              Smartbeeps provides an enterprise communication suite covering SMS, Voice, WhatsApp Business API, RCS, and AI-driven bot services. We grant you a revocable, non-exclusive, non-transferable license to integrate our APIs into your applications solely for legitimate business communications.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">2. Compliance with External Platforms</h2>
            <p className="text-sm text-slate-600">
              Our services operate through third-party providers. By using these channels, you agree to their respective privacy and usage terms:
            </p>
            <ul className="space-y-2 text-sm text-slate-600 list-disc pl-5">
              <li>WhatsApp / Meta: Meta Business Terms and the WhatsApp Business Privacy Policy</li>
              <li>RCS / Google: Google RBM Terms of Service and the Google Privacy Policy</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">3. Account Security &amp; Data Protection</h2>
            <p className="text-sm text-slate-600">
              You are responsible for safeguarding your account credentials and API keys. Provide accurate registration information and notify Smartbeeps immediately of any unauthorized use. Ensure your collection of end-user data complies with applicable data protection laws, including the Digital Personal Data Protection Act (DPDP).
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-900">4. Consent for Communications</h2>
            <div className="space-y-2 text-sm text-slate-600">
              <p>By registering an account or providing contact details on www.smartbeeps.com, you expressly consent to receive communications from Smartbeeps.</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Channels: We may communicate via SMS, WhatsApp, Email, Voice, or RCS.
                </li>
                <li>
                  Content: Communications may include transactional messages (OTPs, service updates, account alerts) and promotional messages (new product features, offers, newsletters).
                </li>
                <li>
                  Opt-Out: You may opt out of promotional communications at any time by following unsubscribe instructions or contacting support.
                </li>
              </ul>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">5. Acceptable Use &amp; Content Restrictions</h2>
            <p className="text-sm text-slate-600">You may not use the Smartbeeps platform for:</p>
            <ul className="list-disc pl-5 space-y-2 text-sm text-slate-600">
              <li>Spamming or sending unsolicited messages without verifiable opt-in consent</li>
              <li>Transmitting illegal, defamatory, fraudulent, or harmful content</li>
              <li>Bypassing rate limits, reverse-engineering the API, or disrupting service integrity</li>
              <li>Violating regional DLT regulations or carrier-specific rules</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">6. Intellectual Property Rights</h2>
            <p className="text-sm text-slate-600">
              The Smartbeeps name, logo, software code, and all website content are the exclusive property of Smartbeeps. You may not reproduce, modify, or redistribute any part of the platform without express written consent.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">7. Service Limitations &amp; Disclaimer</h2>
            <p className="text-sm text-slate-600">
              Smartbeeps acts as a communication gateway relying on third-party mobile network operators. We do not guarantee 100% delivery or specific delivery timestamps. The service is provided on an "as-is" and "as-available" basis, and we disclaim all warranties, express or implied, including merchantability or fitness for a particular purpose.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">8. Limitation of Liability</h2>
            <p className="text-sm text-slate-600">
              To the maximum extent permitted by law, Smartbeeps is not liable for indirect, incidental, or consequential damages, including loss of data or business profits, arising from use of the platform.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">9. Modifications to Terms</h2>
            <p className="text-sm text-slate-600">
              Smartbeeps may update these Terms and Conditions at any time. Continued use of the platform after changes are posted constitutes acceptance of the revised terms.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">10. Governing Law</h2>
            <p className="text-sm text-slate-600">
              These terms are governed by and construed in accordance with the laws of India. Any disputes are subject to the exclusive jurisdiction of the courts in Bengaluru.
            </p>
          </section>

          <section className="space-y-4 border-t border-slate-200 pt-6">
            <h2 className="text-2xl font-bold text-slate-900">11. Contact Information</h2>
            <p className="text-sm text-slate-600">
              For questions about these Terms, contact us at info@smartbeeps.com or visit www.smartbeeps.com/contact.
            </p>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700">
                Contact Us
              </Button>
            </Link>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
